import { useState } from 'react';
import { articlesApi } from '@/app/api/articles';

interface UsePublishArticleOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function usePublishArticle(options?: UsePublishArticleOptions) {
  const [isSlugModalOpen, setIsSlugModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [currentArticleId, setCurrentArticleId] = useState<string | null>(null);
  const [currentArticleTitle, setCurrentArticleTitle] = useState<string>('');
  const [currentSlug, setCurrentSlug] = useState<string | undefined>(undefined);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Wizard State
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardInitialMeta, setWizardInitialMeta] = useState<any>(null);
  const [articleContent, setArticleContent] = useState<string>('');


  const initiatePublish = async (articleId: string) => {
    try {
      // 1. Fetch metadata, content, and tags for validation
      const [article, content, tags] = await Promise.all([
        articlesApi.getArticle(articleId),
        articlesApi.getLiveContent(articleId).catch(() => ''), // Graceful fallback if content missing (e.g. empty)
        articlesApi.getArticleTags(articleId)
      ]);

      setArticleContent(content); // Store for later validation
      setWizardInitialMeta({
        category: article.meta?.category || '',
        tags: tags || article.meta?.tags || [],
        citations: article.meta?.citations || [],
        mentions: article.meta?.mentions?.map((m: any) => typeof m === 'string' ? m : m.name) || [],
        slug: article.slug // Pass existing slug if any
      });

      // 3. Store Article Data
      setCurrentArticleId(articleId);
      setCurrentArticleTitle(article.meta?.title || 'Untitled');
      setPublishError(null);

      // Open Unified Wizard
      setIsWizardOpen(true);
    } catch (error) {
      console.error('Failed to fetch article:', error);
      options?.onError?.(error instanceof Error ? error : new Error('Failed to fetch article'));
    }
  };

  const handleWizardPublish = async (slug: string, meta: any) => {
    if (!currentArticleId) return;

    setIsPublishing(true);
    setPublishError(null);

    try {
      // 1. Update Metadata First
      await articlesApi.updateArticle(currentArticleId, {
        meta: { ...meta }
      });

      // 2. Publish
      await articlesApi.publishArticle(currentArticleId, slug);

      setIsWizardOpen(false);
      setCurrentArticleId(null);
      setCurrentArticleTitle('');
      setWizardInitialMeta(null);
      options?.onSuccess?.();

    } catch (error: any) {
      console.error('Failed to publish article:', error);
      // Check if it's a slug conflict error (409)
      if (error.response?.status === 409 || error.message?.includes('slug')) {
        setPublishError(error.response?.data?.error || error.message || 'Slug already exists. Please choose a different slug.');
      } else {
        setPublishError('Failed to publish article. Please try again.');
        options?.onError?.(error instanceof Error ? error : new Error('Failed to publish article'));
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const closeWizard = () => {
    setIsWizardOpen(false);
    setCurrentArticleId(null);
    setArticleContent('');
  };

  return {
    // State
    isPublishing,
    currentArticleId,
    currentArticleTitle,
    publishError,

    // Actions
    initiatePublish,

    // Wizard State & Actions
    isWizardOpen,
    wizardInitialMeta,
    articleContent,
    handleWizardPublish,
    closeWizard
  };
}
